import { ref } from "vue";
import {
  collection,
  query,
  where,
  orderBy,
  limit as limitFn,
  onSnapshot,
  getDocs,
} from "firebase/firestore";
import { projectFirestore } from "@/firebase/config";

/**
 * documentReference example:
 * {
 *   eventId: { collection: "guests", after: "guests" }
 * }
 *
 * Meaning:
 *   guests.eventId === event.id
 *   result => event.guests = []
 */
const getRelationShipCollection = (documentReference) => {
  const mainDocuments = ref([]);

  const fetchRelatedDocuments = (
    parentCollection,
    parentQueryOptions = {},
    childQueryOptions = {}
  ) => {
    // 🔹 Build parent query (REAL-TIME)
    let parentQuery = query(
      collection(projectFirestore, parentCollection)
    );

    if (parentQueryOptions.whereDoc?.length) {
      parentQuery = query(parentQuery, ...parentQueryOptions.whereDoc);
    }

    if (parentQueryOptions.orderByDoc) {
      parentQuery = query(
        parentQuery,
        orderBy(...parentQueryOptions.orderByDoc)
      );
    }

    if (parentQueryOptions.limitValue) {
      parentQuery = query(
        parentQuery,
        limitFn(parentQueryOptions.limitValue)
      );
    }

    // 🔹 Listen parent only
    const unsubscribe = onSnapshot(parentQuery, async (snapshot) => {
      const parents = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (!parents.length) {
        mainDocuments.value = [];
        return;
      }

      // 🔹 Fetch children (one-to-many)
      for (const parent of parents) {
        for (const key in documentReference) {
          const config = documentReference[key];
          const { collection: childCollection, after, field: customField } = config;
          const field = customField || key; // Use custom field or fallback to key

          let childQuery = query(
            collection(projectFirestore, childCollection),
            where(field, "==", parent.id)
          );

          if (childQueryOptions.whereDoc?.length) {
            childQuery = query(childQuery, ...childQueryOptions.whereDoc);
          }

          if (childQueryOptions.orderByDoc) {
            childQuery = query(
              childQuery,
              orderBy(...childQueryOptions.orderByDoc)
            );
          }

          if (childQueryOptions.limitValue) {
            childQuery = query(
              childQuery,
              limitFn(childQueryOptions.limitValue)
            );
          }

          const childSnap = await getDocs(childQuery);

          parent[after] = childSnap.docs.map(d => ({
            id: d.id,
            ...d.data(),
          }));
        }
      }

      mainDocuments.value = parents;
    });

    return unsubscribe;
  };

  return {
    fetchRelatedDocuments,
    mainDocuments,
  };
};

export default getRelationShipCollection;
